import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { StatusPill, PriorityPill, GhostPill } from '../../components/Pill.tsx';
import type { PrayerNote, ProgressEntry } from '../../domain.ts';
import type { NotePatch } from '../../api/notes.ts';
import { isFuture, planLabel, today } from './shared.ts';

/**
 * C3 · C3-1 — 기도 노트 기본 화면.
 *
 * 여기서 바꿀 수 있는 것은 제목 · 내용 · 기도 진행 기록입니다.
 * 영역 · 상태 · 중요도 · PLAN · 대표 · Private 는 설정 화면(C4) 소관이라,
 * 상태창에는 보이되 여기서는 눌리지 않습니다 — 같은 값을 두 화면에서 바꿀 수
 * 있으면 어느 쪽이 맞는지 알 수 없게 됩니다.
 *
 * 제목과 내용은 버튼 없이 눌러서 바로 고칩니다. 화면만 봐서는 알 수 없는
 * 동작이라 본문 아래에 한 줄로 적어 둡니다.
 */

const HISTORY_LABEL: Record<string, string> = {
  CREATED: '생성',
  TITLE_UPDATED: '제목 수정',
  BODY_UPDATED: '내용 수정',
  STATUS_CHANGED: '상태 변경',
  PRIORITY_CHANGED: '중요도 변경',
  PLAN_CHANGED: 'Plan 변경',
  REPRESENTATIVE_CHANGED: '대표 기도제목 변경',
  PRIVATE_CHANGED: '공개 설정 변경',
  MOVED: '경로 이동',
};

interface Props {
  note: PrayerNote;
  onBack: () => void;
  onSettings: () => void;
  onPatch: (changes: NotePatch) => Promise<boolean>;
}

export function NoteDetail({ note, onBack, onSettings, onPatch }: Props) {
  // 히스토리는 평소 접혀 있습니다 — 시스템 로그가 본문을 밀어내면 안 됩니다
  const [openHistory, setOpenHistory] = useState(false);
  const [editing, setEditing] = useState<ProgressEntry | 'new' | null>(null);
  const [draft, setDraft] = useState<{ title: string; body: string } | null>(null);

  const progress = note.progress;
  // 오래된 것이 위로 오도록. 서버는 붙인 순서 그대로 줍니다
  const log = [...note.history].reverse();

  const saveEntry = async (entry: ProgressEntry) => {
    const next =
      editing === 'new'
        ? [...progress, entry]
        : progress.map((p) => (p.entryId === entry.entryId ? entry : p));
    if (await onPatch({ progress: next })) setEditing(null);
  };

  const saveText = async () => {
    if (!draft) return;
    const changes: NotePatch = {};
    if (draft.title.trim() !== '' && draft.title.trim() !== note.title) {
      changes.title = draft.title.trim();
    }
    if (draft.body !== note.body) changes.body = draft.body;
    if (Object.keys(changes).length > 0) await onPatch(changes);
    setDraft(null);
  };

  return (
    <div className="screen">
      <NavBar
        title="기도 노트"
        subtitle={note.category}
        start={{ icon: 'back', label: '이전', onClick: onBack }}
        end={{ icon: 'more', label: '설정', accent: true, onClick: onSettings }}
      />

      <div className="screen__body">
        {/* 상태 → 대표 → 중요도 → PLAN. 대표는 on/off 를 색으로만 구분합니다 */}
        <div className="card__pills">
          <StatusPill status={note.status} />
          <span className={note.representative ? 'pill pill--rep' : 'pill pill--rep-off'}>대표</span>
          <PriorityPill priority={note.priority} />
          <GhostPill>{planLabel(note.plan)}</GhostPill>
        </div>

        {draft ? (
          <div className="card card--flat">
            <label className="field">
              <span className="field__label">제목</span>
              <input
                className="input"
                value={draft.title}
                autoFocus
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field__label">내용</span>
              <textarea
                className="input input--tall"
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              />
            </label>
            <div className="btn-row">
              <button type="button" className="btn btn--secondary" onClick={() => setDraft(null)}>
                취소
              </button>
              <button
                type="button"
                className="btn btn--primary"
                disabled={draft.title.trim() === ''}
                onClick={saveText}
              >
                저장
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="note__text"
            onClick={() => setDraft({ title: note.title, body: note.body })}
          >
            <span className="note__title">{note.title}</span>
            <span className="card__text">{note.body}</span>
          </button>
        )}

        {/* 영역을 여기서 못 바꾸는 것은 화면만 봐서는 모릅니다 */}
        <p className="hint">
          제목과 내용을 눌러 바로 고칩니다. 영역은 설정 화면에서 옮깁니다.
        </p>

        {/* PLAN 이 Everyday 여도 진행 기록은 나옵니다 */}
        <div className="section-head">
          <h3 className="section-head__title">기도 진행 기록</h3>
          <button type="button" className="section-head__action" onClick={() => setEditing('new')}>
            수정 · + 기록
          </button>
        </div>

        {editing !== null && (
          <ProgressForm
            entry={editing === 'new' ? null : editing}
            onCancel={() => setEditing(null)}
            onSave={saveEntry}
            onDelete={
              editing === 'new'
                ? undefined
                : async () => {
                    await onPatch({
                      progress: progress.filter((p) => p.entryId !== editing.entryId),
                    });
                    setEditing(null);
                  }
            }
          />
        )}

        {progress.length === 0 && editing === null ? (
          <p className="empty__hint">
            아직 기록이 없습니다. 감사한 일이 있으면 여기 바로 적어 두세요.
          </p>
        ) : (
          <ol className="tl">
            {progress.map((s, i) => (
              <li className="tl__item" key={s.entryId}>
                <span className="tl__gutter">
                  <span className={isFuture(s.date) ? 'tl__node tl__node--future' : 'tl__node'} />
                  {i !== progress.length - 1 && <span className="tl__line" />}
                </span>
                <span
                  className={isFuture(s.date) ? 'tl__content tl__content--future' : 'tl__content'}
                >
                  <b>{s.title}</b>
                  <span>
                    {s.date.replace(/-/g, '.')}
                    {isFuture(s.date) ? ' 예정' : ''}
                  </span>
                  {s.body && <span className="tl__body">{s.body}</span>}
                  <button type="button" className="btn btn--inline" onClick={() => setEditing(s)}>
                    수정
                  </button>
                </span>
              </li>
            ))}
          </ol>
        )}

        <div className="section-head">
          <h3 className="section-head__title">기도 히스토리</h3>
          <button
            type="button"
            className="section-head__action section-head__action--quiet"
            aria-expanded={openHistory}
            onClick={() => setOpenHistory((v) => !v)}
          >
            {openHistory ? '접기 ▴' : '펼치기 ▾'}
          </button>
        </div>
        {openHistory &&
          (log.length === 0 ? (
            <p className="empty__hint">아직 변경 기록이 없습니다.</p>
          ) : (
            <div className="hist">
              {log.map((h, i) => (
                <div className="hist__row" key={`${h.at}-${i}`}>
                  <time>{h.at.slice(5, 10).replace('-', '/')}</time>
                  <span>{h.detail ?? HISTORY_LABEL[h.kind] ?? h.kind}</span>
                </div>
              ))}
            </div>
          ))}
      </div>

    </div>
  );
}

/** 진행 기록 항목은 제목 · 날짜 · 내용입니다. 잘못 적을 수 있으므로 수정도 됩니다. */
function ProgressForm({
  entry,
  onSave,
  onCancel,
  onDelete,
}: {
  entry: ProgressEntry | null;
  onSave: (e: ProgressEntry) => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [title, setTitle] = useState(entry?.title ?? '');
  const [date, setDate] = useState(entry?.date ?? today());
  const [body, setBody] = useState(entry?.body ?? '');

  return (
    <div className="card card--flat">
      <label className="field">
        <span className="field__label">제목</span>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">날짜</span>
        <input
          className="input"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field__label">내용</span>
        <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)} />
      </label>
      <div className="btn-row">
        {onDelete && (
          <button type="button" className="btn btn--danger-quiet" onClick={onDelete}>
            삭제
          </button>
        )}
        <button type="button" className="btn btn--secondary" onClick={onCancel}>
          취소
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={title.trim() === ''}
          onClick={() => onSave({ entryId: entry?.entryId ?? '', title: title.trim(), date, body })}
        >
          저장
        </button>
      </div>
    </div>
  );
}
